import { z } from 'zod';

// ---------------------------------------------------------------- общее

export const paperColors = ['lemon', 'apricot', 'pink', 'lilac', 'sky', 'mint', 'sage', 'sand'] as const;
export const PaperColor = z.enum(paperColors);
export type PaperColor = z.infer<typeof PaperColor>;

export const Role = z.enum(['admin', 'teacher', 'student']);
export type Role = z.infer<typeof Role>;

export const ErrorCode = z.enum([
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'CONFLICT',
  'PLAN_LIMIT_REACHED',
  'SCHEDULE_CONFLICT',
  'SUBSCRIPTION_EXPIRED',
  'SCHOOL_UNAVAILABLE',
  'INVALID_CREDENTIALS',
  'INVITE_INVALID',
  'TWO_FACTOR_REQUIRED',
  'INVALID_CODE',
  'RATE_LIMITED',
  'CSRF',
  'INTERNAL',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ApiError = z.object({
  code: ErrorCode,
  status: z.number(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  requestId: z.string().optional(),
});
export type ApiError = z.infer<typeof ApiError>;

/** Заголовок против CSRF: браузер не отправит его с чужого сайта без CORS. */
export const CSRF_HEADER = 'x-aurea-csrf';

const Email = z
  .email()
  .max(254)
  .transform((v) => v.trim().toLowerCase());
const Password = z.string().min(10).max(200);
const Name = z.string().trim().min(1).max(80);

// ---------------------------------------------------------------- школа: публичное и вход

export const SchoolPublic = z.object({
  name: z.string(),
  slug: z.string(),
  accentColor: PaperColor.nullable(),
  logoUrl: z.string().nullable(),
  defaultLocale: z.enum(['ru', 'en']),
  timezone: z.string(),
});
export type SchoolPublic = z.infer<typeof SchoolPublic>;

export const LoginBody = z.object({
  email: Email,
  password: z.string().min(1).max(200),
  remember: z.boolean().default(true),
});
export type LoginBody = z.input<typeof LoginBody>;

export const Me = z.object({
  id: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  roles: z.array(Role),
  isOwner: z.boolean(),
  locale: z.enum(['ru', 'en']),
  timezone: z.string(),
  impersonated: z.boolean(),
  school: z.object({
    name: z.string(),
    slug: z.string(),
    status: z.string(),
    subscriptionEndsAt: z.string(),
    planName: z.string(),
    maxTeachers: z.number().nullable(),
    maxStudents: z.number().nullable(),
    teachers: z.number(),
    students: z.number(),
    accentColor: PaperColor.nullable(),
    logoUrl: z.string().nullable(),
    defaultLocale: z.enum(['ru', 'en']),
    settings: z.lazy(() => SchoolSettings),
  }),
});
export type Me = z.infer<typeof Me>;

// ---------------------------------------------------------------- настройки школы

export const SchoolSettings = z.object({
  chargeAbsent: z.boolean(),
  teacherCanReschedule: z.boolean(),
  showGroupMembers: z.boolean(),
  workdayStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'TIME'),
  workdayEnd: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'TIME'),
  notifyLessonChanges: z.boolean(),
});
export type SchoolSettings = z.infer<typeof SchoolSettings>;

export const SchoolSettingsBody = z
  .object({
    name: z.string().trim().min(2).max(80),
    accentColor: PaperColor.nullable(),
    timezone: z.string().min(3).max(64),
    defaultLocale: z.enum(['ru', 'en']),
    settings: SchoolSettings,
  })
  .refine((b) => b.settings.workdayStart < b.settings.workdayEnd, {
    path: ['settings', 'workdayEnd'],
    message: 'WORKDAY_ORDER',
  });
export type SchoolSettingsBody = z.infer<typeof SchoolSettingsBody>;

/** Логотип как data URL: PNG, JPEG или WebP до 256 КБ. SVG не принимаем — в нём может быть скрипт. */
export const LOGO_MAX_BYTES = 256 * 1024;
export const LogoBody = z.object({
  dataUrl: z
    .string()
    .max(Math.ceil((LOGO_MAX_BYTES * 4) / 3) + 64)
    .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/, 'LOGO_FORMAT'),
});
export type LogoBody = z.infer<typeof LogoBody>;

// ---------------------------------------------------------------- каталог

export const Discipline = z.object({
  id: z.uuid(),
  name: z.string(),
  color: PaperColor,
  courses: z.number(),
});
export type Discipline = z.infer<typeof Discipline>;
export const DisciplineBody = z.object({ name: Name, color: PaperColor });

export const Course = z.object({
  id: z.uuid(),
  name: z.string(),
  disciplineId: z.uuid(),
  disciplineName: z.string(),
  color: PaperColor,
  format: z.enum(['individual', 'group', 'mixed']),
  defaultDurationMin: z.number(),
  teachers: z.array(z.object({ id: z.uuid(), name: z.string() })),
  groups: z.number(),
  students: z.number(),
});
export type Course = z.infer<typeof Course>;
export const CourseBody = z.object({
  disciplineId: z.uuid(),
  name: Name,
  format: z.enum(['individual', 'group', 'mixed']),
  defaultDurationMin: z.number().int().min(15).max(480),
  teacherIds: z.array(z.uuid()).default([]),
});

export const Group = z.object({
  id: z.uuid(),
  name: z.string(),
  courseId: z.uuid(),
  courseName: z.string(),
  color: PaperColor,
  students: z.number(),
  capacity: z.number().nullable(),
  teacherId: z.uuid().nullable(),
  teacherName: z.string().nullable(),
});
export type Group = z.infer<typeof Group>;
export const GroupBody = z.object({
  courseId: z.uuid(),
  name: z.string().trim().min(1).max(80),
  teacherId: z.uuid().nullable().default(null),
  capacity: z.number().int().min(1).max(500).nullable().default(null),
});
export type GroupBody = z.input<typeof GroupBody>;
export const GroupPatch = GroupBody.omit({ courseId: true });
export type GroupPatch = z.input<typeof GroupPatch>;
export const GroupDetails = Group.extend({
  members: z.array(
    z.object({ enrollmentId: z.uuid(), studentId: z.uuid(), name: z.string(), since: z.string() }),
  ),
});
export type GroupDetails = z.infer<typeof GroupDetails>;

/** Запись ученика на курс (в группу или индивидуально). */
export const EnrollBody = z.object({
  studentId: z.uuid(),
  courseId: z.uuid(),
  groupId: z.uuid().nullable().default(null),
});
export type EnrollBody = z.input<typeof EnrollBody>;
/** Перевод: в другую группу (того же или другого курса) или из группы в индивидуальные. */
export const MoveEnrollmentBody = z.object({ groupId: z.uuid().nullable() });
export type MoveEnrollmentBody = z.infer<typeof MoveEnrollmentBody>;

// ---------------------------------------------------------------- люди

export const UserListItem = z.object({
  id: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  roles: z.array(Role),
  status: z.enum(['active', 'blocked', 'archived']),
  isOwner: z.boolean(),
  lastLoginAt: z.string().nullable(),
  courses: z.array(z.object({ name: z.string(), color: PaperColor })),
});
export type UserListItem = z.infer<typeof UserListItem>;
export const UsersQuery = z.object({
  role: Role.optional(),
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'blocked', 'archived']).optional(),
});
export const UserBody = z.object({
  firstName: Name,
  lastName: Name,
  email: Email,
  phone: z.string().trim().max(32).optional(),
  roles: z.array(Role).min(1),
});
export type UserBody = z.input<typeof UserBody>;
export const UserPatch = UserBody.extend({ phone: z.string().trim().max(32).nullable().optional() });
export type UserPatch = z.input<typeof UserPatch>;

/** Импорт учеников из CSV: строки уже разобраны в браузере. Курс или группа — по желанию. */
export const ImportStudentsBody = z.object({
  rows: z
    .array(
      z.object({
        firstName: Name,
        lastName: Name,
        email: Email,
        phone: z.string().trim().max(32).optional(),
      }),
    )
    .min(1)
    .max(1000),
  courseId: z.uuid().optional(),
  groupId: z.uuid().optional(),
  sendInvites: z.boolean().default(true),
});
export type ImportStudentsBody = z.input<typeof ImportStudentsBody>;
export const ImportResult = z.object({
  created: z.number(),
  /** Строки, которые не создали: такой e-mail уже есть в школе или повторяется в файле. */
  skipped: z.array(z.object({ row: z.number(), email: z.string(), reason: z.enum(['EXISTS', 'DUPLICATE']) })),
});
export type ImportResult = z.infer<typeof ImportResult>;
export const UserStatusBody = z.object({ status: z.enum(['active', 'blocked', 'archived']) });
export type UserStatusBody = z.infer<typeof UserStatusBody>;
export const UserStatusResult = z.object({
  /** Будущие занятия учителя, которым нужна замена. */
  futureLessons: z.number(),
  /** Курсы, с которых ученик отчислен при переносе в архив. */
  endedEnrollments: z.number(),
});
export type UserStatusResult = z.infer<typeof UserStatusResult>;

// ---------------------------------------------------------------- приглашения

export const Invite = z.object({
  id: z.uuid(),
  label: z.string(),
  role: Role,
  courseName: z.string().nullable(),
  groupName: z.string().nullable(),
  expiresAt: z.string().nullable(),
  maxUses: z.number().nullable(),
  usedCount: z.number(),
  revoked: z.boolean(),
  createdAt: z.string(),
});
export type Invite = z.infer<typeof Invite>;
export const InviteBody = z.object({
  label: z.string().trim().min(1).max(120),
  role: z.enum(['student', 'teacher']).default('student'),
  groupId: z.uuid().optional(),
  courseId: z.uuid().optional(),
  expiresInDays: z.number().int().min(1).max(365).optional(),
  maxUses: z.number().int().min(1).max(10_000).optional(),
});
export type InviteBody = z.input<typeof InviteBody>;
export const InviteCreated = Invite.extend({ url: z.string() });

export const JoinInfo = z.object({
  school: SchoolPublic,
  label: z.string(),
  role: Role,
  courseName: z.string().nullable(),
  groupName: z.string().nullable(),
});
export type JoinInfo = z.infer<typeof JoinInfo>;
export const JoinBody = z.object({
  firstName: Name,
  lastName: Name,
  email: Email,
  phone: z.string().trim().max(32).optional(),
  password: Password,
  consent: z.literal(true),
});
export type JoinBody = z.input<typeof JoinBody>;

// ---------------------------------------------------------------- расписание

export const Lesson = z.object({
  id: z.uuid(),
  startsAt: z.string(),
  endsAt: z.string(),
  status: z.enum(['scheduled', 'completed', 'cancelled']),
  title: z.string(),
  color: PaperColor,
  courseId: z.uuid(),
  groupId: z.uuid().nullable(),
  groupName: z.string().nullable(),
  teacher: z.object({ id: z.uuid(), name: z.string() }),
  /** Имя ученика для индивидуального занятия, иначе null (тогда показываем группу). */
  studentName: z.string().nullable(),
  participants: z.number(),
  unmarked: z.number(),
  location: z.string().nullable(),
  onlineUrl: z.string().nullable(),
});
export type Lesson = z.infer<typeof Lesson>;
export const LessonsQuery = z.object({
  from: z.iso.datetime({ offset: true }),
  to: z.iso.datetime({ offset: true }),
  teacherId: z.uuid().optional(),
});
export const LessonMoveBody = z.object({
  startsAt: z.iso.datetime({ offset: true }),
  teacherId: z.uuid().optional(),
  durationMin: z.number().int().min(15).max(480).optional(),
});
export type LessonMoveBody = z.infer<typeof LessonMoveBody>;

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'DATE');
const HHmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'TIME');

/** Повтор по неделям: дни недели 0 = понедельник … 6 = воскресенье. */
export const Repeat = z
  .object({
    weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    intervalWeeks: z.number().int().min(1).max(4).default(1),
    until: IsoDate.optional(),
    count: z.number().int().min(1).max(200).optional(),
  })
  .refine((r) => !(r.until && r.count), 'UNTIL_OR_COUNT');
export type Repeat = z.input<typeof Repeat>;

/** Изменение серии с этого занятия («это и следующие») или всех будущих занятий серии. */
export const SeriesChangeBody = z.object({
  scope: z.enum(['following', 'all']),
  startTime: HHmm,
  durationMin: z.number().int().min(15).max(480),
  teacherId: z.uuid(),
});
export type SeriesChangeBody = z.infer<typeof SeriesChangeBody>;

export const LessonCreateBody = z
  .object({
    courseId: z.uuid(),
    groupId: z.uuid().optional(),
    studentId: z.uuid().optional(),
    teacherId: z.uuid(),
    date: IsoDate,
    startTime: HHmm,
    durationMin: z.number().int().min(15).max(480),
    location: z.string().trim().max(120).optional(),
    onlineUrl: z
      .url({ protocol: /^https$/ })
      .max(500)
      .optional(),
    repeat: Repeat.optional(),
  })
  .refine((b) => Boolean(b.groupId) !== Boolean(b.studentId), 'GROUP_OR_STUDENT');
export type LessonCreateBody = z.input<typeof LessonCreateBody>;

export const LessonConflict = z.object({
  date: z.string(),
  title: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
});
export const LessonCreated = z.object({
  created: z.number(),
  seriesId: z.uuid().nullable(),
  firstLessonId: z.uuid().nullable(),
});

export const CancelBody = z.object({
  reason: z.string().trim().max(300).optional(),
  scope: z.enum(['this', 'following']).default('this'),
});
export type CancelBody = z.input<typeof CancelBody>;

export const Attendance = z.enum(['unmarked', 'present', 'absent', 'excused']);
export const LessonDetails = Lesson.extend({
  seriesId: z.uuid().nullable(),
  cancelReason: z.string().nullable(),
  participantsList: z.array(z.object({ studentId: z.uuid(), name: z.string(), attendance: Attendance })),
});
export type LessonDetails = z.infer<typeof LessonDetails>;
export const CompleteBody = z.object({
  attendance: z.array(z.object({ studentId: z.uuid(), attendance: Attendance.exclude(['unmarked']) })),
});
export type CompleteBody = z.infer<typeof CompleteBody>;

export const Teacher = z.object({ id: z.uuid(), firstName: z.string(), lastName: z.string() });
export type Teacher = z.infer<typeof Teacher>;

// ---------------------------------------------------------------- главная

export const Dashboard = z.object({
  today: z.array(Lesson),
  unmarked: z.array(Lesson),
});
export type Dashboard = z.infer<typeof Dashboard>;

// ---------------------------------------------------------------- платформа

export const PlatformLoginBody = z.object({ email: Email, password: z.string().min(1).max(200) });
export const PlatformMe = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  /** Пока 2FA не настроена, админка показывает только экран настройки. */
  twoFactorEnabled: z.boolean(),
});
export const PlatformLoginResult = z.object({
  twoFactor: z.enum(['required', 'setup']),
  ticket: z.string().optional(),
});
export const TotpSetup = z.object({ secret: z.string(), uri: z.string() });
export const TotpCodeBody = z.object({ code: z.string().regex(/^\d{6}$/, 'CODE') });
export type PlatformMe = z.infer<typeof PlatformMe>;

export const Plan = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  maxTeachers: z.number().nullable(),
  maxStudents: z.number().nullable(),
  listPrice: z.number(),
});
export type Plan = z.infer<typeof Plan>;

export const PlatformSchool = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(['trial', 'active', 'grace', 'expired', 'suspended', 'deleted']),
  planCode: z.string(),
  planName: z.string(),
  priceMonthly: z.number(),
  subscriptionEndsAt: z.string(),
  daysLeft: z.number(),
  teachers: z.number(),
  students: z.number(),
  maxTeachers: z.number().nullable(),
  maxStudents: z.number().nullable(),
  hosts: z.array(z.string()),
  ownerEmail: z.string().nullable(),
  createdAt: z.string(),
});
export type PlatformSchool = z.infer<typeof PlatformSchool>;

export const SchoolDomain = z.object({
  id: z.uuid(),
  host: z.string(),
  kind: z.enum(['subdomain', 'custom']),
  isPrimary: z.boolean(),
  status: z.enum(['pending_dns', 'active', 'disabled']),
  lastCheckAt: z.string().nullable(),
  lastCheckError: z.string().nullable(),
});
export type SchoolDomain = z.infer<typeof SchoolDomain>;

/** Карточка школы в админке: всё о школе, домены, оплаты подписки и журнал действий операторов. */
export const PlatformSchoolCard = PlatformSchool.extend({
  planId: z.uuid(),
  timezone: z.string(),
  defaultLocale: z.string(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  notes: z.string().nullable(),
  maxTeachersOverride: z.number().nullable(),
  maxStudentsOverride: z.number().nullable(),
  domains: z.array(SchoolDomain),
  periods: z.array(
    z.object({
      id: z.uuid(),
      startsAt: z.string(),
      endsAt: z.string(),
      days: z.number(),
      amount: z.number(),
      method: z.string(),
      paidAt: z.string(),
      comment: z.string().nullable(),
    }),
  ),
  audit: z.array(
    z.object({
      id: z.uuid(),
      action: z.string(),
      adminName: z.string().nullable(),
      diff: z.unknown(),
      createdAt: z.string(),
    }),
  ),
});
export type PlatformSchoolCard = z.infer<typeof PlatformSchoolCard>;

export const SchoolPatchBody = z.object({
  name: z.string().trim().min(2).max(80),
  planId: z.uuid(),
  priceMonthly: z.number().int().min(0),
  maxTeachersOverride: z.number().int().min(0).nullable(),
  maxStudentsOverride: z.number().int().min(0).nullable(),
  contactName: z.string().trim().max(120).nullable(),
  contactEmail: z.string().trim().max(200).nullable(),
  contactPhone: z.string().trim().max(40).nullable(),
  notes: z.string().trim().max(4000).nullable(),
});
export type SchoolPatchBody = z.infer<typeof SchoolPatchBody>;
export const SuspendBody = z.object({ suspended: z.boolean() });
export type SuspendBody = z.infer<typeof SuspendBody>;

/** Собственный домен школы: обычное имя хоста, не поддомен платформы. */
export const DomainBody = z.object({
  host: z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'HOST_FORMAT'),
});
export type DomainBody = z.infer<typeof DomainBody>;

const reservedSlugs = [
  'admin',
  'api',
  'www',
  'app',
  'mail',
  'static',
  'cdn',
  'status',
  'help',
  'docs',
  'billing',
  'auth',
  'auto',
  'hr',
  'smtp',
  'imap',
  'ftp',
  'schools',
];
export const Slug = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9](-?[a-z0-9])*$/, 'SLUG_FORMAT')
  .refine((v) => !reservedSlugs.includes(v), 'SLUG_RESERVED');

export const ApplicationBody = z.object({
  schoolName: z.string().trim().min(2).max(120),
  desiredSlug: z.string().trim().toLowerCase().max(40).optional(),
  contactName: z.string().trim().min(2).max(120),
  email: Email,
  phone: z.string().trim().max(32).optional(),
  size: z.enum(['solo', 'small', 'medium', 'large']).optional(),
  message: z.string().trim().max(2000).optional(),
  /** Поле-ловушка: люди его не видят, боты заполняют. */
  website: z.string().max(200).optional(),
});
export type ApplicationBody = z.input<typeof ApplicationBody>;

export const Application = z.object({
  id: z.uuid(),
  schoolName: z.string(),
  desiredSlug: z.string().nullable(),
  contactName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  size: z.string().nullable(),
  message: z.string().nullable(),
  status: z.enum(['new', 'approved', 'rejected']),
  schoolId: z.uuid().nullable(),
  createdAt: z.string(),
});
export type Application = z.infer<typeof Application>;

export const CreateSchoolBody = z.object({
  name: z.string().trim().min(2).max(120),
  slug: Slug,
  planId: z.uuid(),
  priceMonthly: z.number().int().min(0),
  trialDays: z.number().int().min(0).max(90).default(14),
  timezone: z.string().default('Europe/Moscow'),
  defaultLocale: z.enum(['ru', 'en']).default('ru'),
  owner: z.object({ firstName: Name, lastName: Name, email: Email }),
  /** Заявка, из которой подключают школу: она станет «одобрена». */
  applicationId: z.uuid().optional(),
});
export type CreateSchoolBody = z.input<typeof CreateSchoolBody>;
export const SchoolCreated = z.object({ id: z.uuid(), url: z.string(), ownerSetupUrl: z.string() });

export const ExtendBody = z.object({
  days: z.number().int().min(1).max(730),
  amount: z.number().int().min(0),
  method: z.enum(['invoice', 'transfer', 'card', 'cash', 'other']),
  paidAt: z.iso.datetime({ offset: true }).optional(),
  comment: z.string().max(500).optional(),
});
export type ExtendBody = z.input<typeof ExtendBody>;

// ---------------------------------------------------------------- абонементы и оплаты

export const Money = z.number().int().min(0).max(100_000_000_00);
export const PaymentMethod = z.enum(['cash', 'card', 'transfer', 'other']);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const PackageTemplate = z.object({
  id: z.uuid(),
  name: z.string(),
  disciplineId: z.uuid().nullable(),
  courseId: z.uuid().nullable(),
  /** На что действует: название курса или дисциплины; null — на любые занятия. */
  scopeName: z.string().nullable(),
  color: PaperColor.nullable(),
  lessonsCount: z.number(),
  price: z.number(),
  validityDays: z.number().nullable(),
  sold: z.number(),
});
export type PackageTemplate = z.infer<typeof PackageTemplate>;
export const PackageTemplateBody = z.object({
  name: z.string().trim().min(1).max(120),
  disciplineId: z.uuid().optional(),
  courseId: z.uuid().optional(),
  lessonsCount: z.number().int().min(1).max(500),
  price: Money,
  validityDays: z.number().int().min(1).max(730).optional(),
});
export type PackageTemplateBody = z.input<typeof PackageTemplateBody>;

export const StudentPackage = z.object({
  id: z.uuid(),
  name: z.string(),
  scopeName: z.string().nullable(),
  color: PaperColor.nullable(),
  lessonsTotal: z.number(),
  lessonsUsed: z.number(),
  price: z.number(),
  paid: z.number(),
  startsOn: z.string(),
  validUntil: z.string().nullable(),
  status: z.enum(['active', 'exhausted', 'expired', 'cancelled']),
  createdAt: z.string(),
});
export type StudentPackage = z.infer<typeof StudentPackage>;

export const SellPackageBody = z.object({
  templateId: z.uuid().optional(),
  /** Без шаблона — абонемент вручную. */
  name: z.string().trim().min(1).max(120).optional(),
  disciplineId: z.uuid().optional(),
  courseId: z.uuid().optional(),
  lessonsTotal: z.number().int().min(1).max(500).optional(),
  price: Money.optional(),
  startsOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  validityDays: z.number().int().min(1).max(730).optional(),
  payment: z.object({ amount: Money, method: PaymentMethod }).optional(),
});
export type SellPackageBody = z.input<typeof SellPackageBody>;
export const PackageSold = z.object({ id: z.uuid(), coveredDebt: z.number() });

export const AdjustBody = z.object({
  delta: z
    .number()
    .int()
    .min(-500)
    .max(500)
    .refine((v) => v !== 0, 'ZERO'),
  reason: z.string().trim().min(3).max(300),
});
export type AdjustBody = z.infer<typeof AdjustBody>;

export const Payment = z.object({
  id: z.uuid(),
  amount: z.number(),
  method: PaymentMethod,
  paidAt: z.string(),
  comment: z.string().nullable(),
  packageName: z.string().nullable(),
});
export type Payment = z.infer<typeof Payment>;
export const PaymentBody = z.object({
  amount: Money.refine((v) => v > 0, 'ZERO'),
  method: PaymentMethod,
  studentPackageId: z.uuid().optional(),
  paidAt: z.iso.datetime({ offset: true }).optional(),
  comment: z.string().trim().max(300).optional(),
});
export type PaymentBody = z.input<typeof PaymentBody>;

export const StudentCard = z.object({
  id: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  status: z.enum(['active', 'blocked', 'archived']),
  isOwner: z.boolean(),
  roles: z.array(Role),
  courses: z.array(
    z.object({
      enrollmentId: z.uuid(),
      courseId: z.uuid(),
      groupId: z.uuid().nullable(),
      name: z.string(),
      color: PaperColor,
      groupName: z.string().nullable(),
    }),
  ),
  packages: z.array(StudentPackage),
  payments: z.array(Payment),
  /** Оплачено минус стоимость абонементов: отрицательное — ученик должен. */
  balance: z.number(),
  /** Проведённые занятия, которые не из чего было списать. */
  debtLessons: z.number(),
  history: z.array(
    z.object({
      lessonId: z.uuid(),
      startsAt: z.string(),
      title: z.string(),
      color: PaperColor,
      attendance: Attendance,
      charged: z.boolean(),
      packageName: z.string().nullable(),
    }),
  ),
});
export type StudentCard = z.infer<typeof StudentCard>;

export const BillingAttention = z.object({
  expiring: z.array(
    z.object({
      studentId: z.uuid(),
      studentName: z.string(),
      packageName: z.string(),
      color: PaperColor.nullable(),
      left: z.number(),
      total: z.number(),
      validUntil: z.string().nullable(),
    }),
  ),
  debtors: z.array(
    z.object({ studentId: z.uuid(), studentName: z.string(), debtLessons: z.number(), balance: z.number() }),
  ),
});
export type BillingAttention = z.infer<typeof BillingAttention>;
